import axios from "axios";
import { PublicRoutes } from "../models/routes";

type HeaderBag = Record<string, unknown>;

function getBridgeTokenFromHash(): string | null {
  try {
    if (!window.location.hash) return null;
    const raw = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
    return new URLSearchParams(raw).get("token");
  } catch {
    return null;
  }
}

function getHeaderValue(headers: unknown, name: string): string | null {
  if (!headers) return null;
  const bag = headers as HeaderBag;
  const direct = bag[name] ?? bag[name.toLowerCase()];
  return typeof direct === "string" ? direct : null;
}

function setHeaderValue(headers: unknown, name: string, value: string): unknown {
  if (!headers) return { [name]: value };

  const maybeSet = (headers as { set?: (k: string, v: string) => void }).set;
  if (typeof maybeSet === "function") {
    maybeSet(name, value);
    return headers;
  }

  const bag = headers as HeaderBag;
  bag[name] = value;
  return headers;
}

//Interceptor para redirigir al login en caso de que haya expirado el token del usuario
export const AxiosInterceptor = () => {
  axios.interceptors.request.use((config) => {
    const existing = getHeaderValue(config.headers, "Authorization");
    const existingLooksBad =
      !existing || !existing.startsWith("Bearer ") || existing.includes("{") || existing.length < "Bearer x.y.z".length;
    if (existingLooksBad) {
      try {
        const raw = localStorage.getItem("token");
        const bridgeToken = typeof window !== "undefined" ? getBridgeTokenFromHash() : null;
        if (!raw && bridgeToken) {
          // Permite que requests tempranos durante /sso-bridge ya salgan con Authorization,
          // antes de que el bridge termine de persistir en localStorage.
          const tokenState = JSON.stringify({ status: 1, token: bridgeToken });
          localStorage.setItem("token", tokenState);
        }

        const rawAfter = localStorage.getItem("token");
        if (rawAfter) {
          let token: string | null = rawAfter;
          try {
            const parsed = JSON.parse(rawAfter);
            token = typeof parsed?.token === "string" ? parsed.token : rawAfter;
          } catch {
            token = rawAfter;
          }
          if (token) {
            config.headers = setHeaderValue(config.headers, "Authorization", `Bearer ${token}`) as typeof config.headers;
            axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          }
        }
      } catch {
        // ignore
      }
    }

    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        // Durante el bridge, evitamos forzar /login por 401 para no romper el flujo antes
        // de que termine de persistir token/user y navegar al dashboard.
        if (typeof window !== "undefined" && window.location.pathname.startsWith("/sso-bridge")) {
          return Promise.reject(error);
        }
        // Limpiar token/user del localStorage para que el reload no re-dispare requests
        // autenticados con un token ya vencido (loop de redirección al login).
        try {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        } catch {
          // ignore
        }
        window.location.replace(`/${PublicRoutes.LOGIN}`);
      } else {
        return Promise.reject(error);
      }
    }
  );
};
